import type { DatasetRow, FlowId, Run, RunFile, RunId, RunStep, RestartRequestStepName } from '@grafx-vdp/shared';

import { isRunningStandalone } from '@/handshake/mock-host.js';

import { ApiError, apiFetch, buildUrl } from './client.js';

export interface ListRunsResponse {
  runs: Run[];
}

export interface ListRunsParams {
  flowId: FlowId;
  limit?: number;
}

export function listRuns(jwt: string | null, params: ListRunsParams): Promise<ListRunsResponse> {
  return apiFetch<ListRunsResponse>('/api/runs', {
    jwt,
    method: 'GET',
    query: { flowId: params.flowId, limit: params.limit },
  });
}

export interface GetRunResponse {
  run: Run;
  steps: RunStep[];
}

export function getRun(jwt: string | null, runId: RunId): Promise<GetRunResponse> {
  return apiFetch<GetRunResponse>(`/api/runs/${encodeURIComponent(runId)}`, {
    jwt,
    method: 'GET',
  });
}

export function cancelRun(jwt: string | null, runId: RunId): Promise<GetRunResponse> {
  return apiFetch<GetRunResponse>(`/api/runs/${encodeURIComponent(runId)}/cancel`, {
    jwt,
    method: 'POST',
  });
}

export interface DeleteRunResponse {
  deleted: true;
  runId: RunId;
  artifactsDeleted: number;
}

export function deleteRun(jwt: string | null, runId: RunId): Promise<DeleteRunResponse> {
  return apiFetch<DeleteRunResponse>(`/api/runs/${encodeURIComponent(runId)}`, {
    jwt,
    method: 'DELETE',
  });
}

export interface RestartRunRequest {
  fromStep: RestartRequestStepName;
}

export function restartRun(
  jwt: string | null,
  runId: RunId,
  body: RestartRunRequest,
): Promise<GetRunResponse> {
  return apiFetch<GetRunResponse>(`/api/runs/${encodeURIComponent(runId)}/restart`, {
    jwt,
    method: 'POST',
    json: body,
  });
}

export interface GetRunDatasetResponse {
  datasetId: string;
  total: number;
  offset: number;
  limit: number;
  columns: string[];
  rows: DatasetRow[];
}

export interface GetRunDatasetParams {
  offset?: number;
  limit?: number;
}

export function getRunDataset(
  jwt: string | null,
  runId: RunId,
  params: GetRunDatasetParams = {},
): Promise<GetRunDatasetResponse> {
  return apiFetch<GetRunDatasetResponse>(`/api/runs/${encodeURIComponent(runId)}/dataset`, {
    jwt,
    method: 'GET',
    query: { offset: params.offset, limit: params.limit },
  });
}

export interface ListRunFilesResponse {
  files: RunFile[];
}

export function listRunFiles(jwt: string | null, runId: RunId): Promise<ListRunFilesResponse> {
  return apiFetch<ListRunFilesResponse>(`/api/runs/${encodeURIComponent(runId)}/files`, {
    jwt,
    method: 'GET',
  });
}

/** Above this size, blob fallback is refused — use the streaming save path instead. */
const BLOB_FALLBACK_MAX_BYTES = 10 * 1024 * 1024;

function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

interface DownloadTokenResponse {
  token: string;
  expiresInSeconds: number;
}

/**
 * Download via a signed URL opened in a new tab. Required when embedded in a
 * cross-origin iframe — file pickers and programmatic blob saves are blocked there.
 */
async function downloadRunFileViaToken(
  jwt: string | null,
  runId: RunId,
  key: string,
): Promise<void> {
  const { token } = await apiFetch<DownloadTokenResponse>(
    `/api/runs/${encodeURIComponent(runId)}/files/download-token`,
    { jwt, method: 'POST', json: { key } },
  );

  const url = buildUrl(
    `/api/runs/${encodeURIComponent(runId)}/files/download/${encodeURIComponent(token)}`,
    { key },
  );

  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    throw new Error('Download blocked — allow pop-ups for this site and try again.');
  }
}

/**
 * Download a run artifact by streaming the API response to disk.
 *
 * When embedded in the Kadanza iframe, uses a short-lived signed URL in a new tab
 * (cross-origin subframes cannot call `showSaveFilePicker`). In standalone mode,
 * uses the File System Access API when available so large ZIPs never pass through
 * `response.blob()`.
 */
export async function downloadRunFile(
  jwt: string | null,
  runId: RunId,
  key: string,
  options?: { filename?: string; sizeBytes?: number },
): Promise<void> {
  if (!isRunningStandalone()) {
    await downloadRunFileViaToken(jwt, runId, key);
    return;
  }

  const filename = options?.filename ?? key.slice(key.lastIndexOf('/') + 1);
  const url = buildUrl(`/api/runs/${encodeURIComponent(runId)}/files/content`, { key });
  const headers: Record<string, string> = {};
  if (jwt) headers.Authorization = `Bearer ${jwt}`;

  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new ApiError(response.status, null, text || response.statusText, text);
  }

  if (!response.body) {
    throw new Error('Download response had no body');
  }

  const showSaveFilePicker = (
    window as Window & {
      showSaveFilePicker?: (options: { suggestedName: string }) => Promise<FileSystemFileHandle>;
    }
  ).showSaveFilePicker;

  if (showSaveFilePicker) {
    const handle = await showSaveFilePicker({ suggestedName: filename });
    await response.body.pipeTo(await handle.createWritable());
    return;
  }

  const sizeBytes =
    options?.sizeBytes ?? Number(response.headers.get('content-length') ?? 0);
  if (sizeBytes > BLOB_FALLBACK_MAX_BYTES) {
    throw new Error(
      'This file is too large to download in this browser. Use Chrome or Edge, which support streaming saves.',
    );
  }

  const blob = await response.blob();
  triggerBlobDownload(blob, filename);
}
